import { MUSCLE_SYNONYMS } from "./muscleSynonyms";
import { isCanonicalMuscle } from "./muscles";

/**
 * 🔴 CAN THE ENGINE READ THIS MUSCLE SPELLING? (W54, 2026-09-22)
 *
 * This is the question `muscleLabel` was RETRACTED from answering on 2026-09-18, and its header
 * said exactly why: `CANONICAL_MUSCLES` is the Swift enum's **24 cases**, while `MuscleNormalizer`
 * also carries a **synonym table**, so the web was calling `Chest`, `Core` and `Shoulders`
 * unreadable without being able to know. MEASURED on the live library: **152 of 253 values, 60.1%**
 * failed the 24-case test, and every screen that drew a warning from it was drawing noise.
 *
 * `muscleSynonyms.ts` is that missing half, GENERATED from the same Swift file. What is below is
 * the lookup itself, ported from `MuscleNormalizer.canonical(for:)` — lowercase, strip
 * punctuation, singularise, try the exact key, then scan phrases longest-first for a substring
 * (a phrase of ≤ 3 characters only as a whole word — E2, below).
 *
 * ⚠️ THE PORT IS FAITHFUL FOR THE BOOLEAN AND DELIBERATELY NOT FOR THE IDENTITY, which is stated
 * rather than discovered later: Swift breaks equal-length ties on the scan path by DICTIONARY
 * order, which is hash-ordered and not deterministic between two runs of the phone — its own
 * comment says so. A tie still resolves to SOMETHING on both sides, so "readable or not" is exactly
 * as true here as there. Anything wanting to know WHICH muscle must ask the engine.
 */

/**
 * Split camelCase, lowercase, strip punctuation, collapse whitespace, singularise —
 * `normalise(_:)`, ported.
 *
 * 🔴 THE camelCase SPLIT FOLLOWS THE SWIFT FIX (W55, `863c61ae`, 2026-09-22). Until then this
 * port reproduced the engine's defect ON PURPOSE — `lowerBack` → Lats — because a web that fixed
 * it alone would disagree with the engine about a live column. The Swift changed, so the twin
 * follows it; left behind, it became the disagreement it was written to prevent. The split runs
 * BEFORE lowercasing, or the case boundary it matches on is already gone.
 */
export function normaliseMuscle(raw: string): string {
  const spaced = raw.replace(/([a-z0-9])([A-Z])/g, "$1 $2");
  const cleaned = [...spaced.toLowerCase()]
    .map((ch) => (/[\p{L}\p{N} ]/u.test(ch) ? ch : " "))
    .join("");
  return cleaned.split(" ").filter((w) => w !== "").map(singularise).join(" ");
}

/** `singularise(_:)`, ported verbatim — crude on purpose; the vocabulary is small and known. */
function singularise(word: string): string {
  if (word.endsWith("ies") && word.length > 4) return `${word.slice(0, -3)}y`;
  if (word.endsWith("s") && !word.endsWith("ss") && word.length > 3) return word.slice(0, -1);
  return word;
}

/* Longest phrase first, so "lateral deltoid" wins over "deltoid" — computed ONCE, which is the
   same fix the Swift side took after measuring that the miss path is the only path for 102 of the
   pool's 103 distinct spellings. */
const PHRASES_BY_LENGTH_DESC: readonly string[] =
  Object.keys(MUSCLE_SYNONYMS).sort((a, b) => b.length - a.length);

/**
 * 🔴 THE SHORT-SUBSTRING RULE FOLLOWS THE SWIFT (E2, 2026-10-18). A phrase of ≤ 3 characters
 * ("ab", "lat", "pec", "hip", …) matches only as a WHOLE WORD of the key; longer phrases keep
 * `includes`. The raw scan read `ab` inside "stabilizer" — five live rows credited Abs. The Swift
 * gained the rule (`MuscleNormalizer.scan`), so the twin follows it, as it followed W55.
 */
const SHORT_PHRASE_MAX_LENGTH = 3;

/** The label the engine would resolve this spelling to, or null. */
export function engineMuscleLabel(raw: string): string | null {
  const key = normaliseMuscle(raw);
  if (key === "") return null;
  const exact = MUSCLE_SYNONYMS[key];
  if (exact !== undefined) return exact;
  const words = new Set(key.split(" "));
  for (const phrase of PHRASES_BY_LENGTH_DESC) {
    const hit = phrase.length <= SHORT_PHRASE_MAX_LENGTH ? words.has(phrase) : key.includes(phrase);
    if (hit) return MUSCLE_SYNONYMS[phrase] ?? null;
  }
  return null;
}

/**
 * Is this a spelling the engine's readers can use?
 *
 * ⚠️ BOTH HALVES. Before W55 the first was load-bearing: of the 24 cases' 48 spellings exactly
 * ONE — the camelCase key `rotatorCuff` — could not be resolved by the synonym table, because
 * `normalise` had no separator to split it on. The camelCase split closed that on BOTH sides, so
 * today the synonym half answers all 48 (a test asserts it). The case half stays as the cheap,
 * exact answer for the column's own keys, and it costs nothing to keep.
 */
export function engineReadsMuscle(raw: string): boolean {
  return isCanonicalMuscle(raw) || engineMuscleLabel(raw) !== null;
}
